/**
 * usePermission — quyền theo controller; lọc listRole theo PH đang mở.
 *
 * Logic: filterListRoleByPhanHe(useGetPhanHe / storage) trước khi match.
 * Hàm: getModulePermission, getOrgScopeModulePermission, getVanHoaModulePermission,
 * usePermission, pickPrimaryModuleRole, mapRoleToPermission, hasControllerPageAccess.
 */
import { useMemo } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import { expandVanHoaMenuModules, roleMatchesVanHoaMenuModules } from '@/utils/vanHoaPermission';
import { filterListRoleByPhanHe } from '@/utils/phanHeRoles';
import { getPhanHeFromStorage } from '@/utils/phanHeStorage';
import { useGetPhanHe } from '@/hooks/useGetPhanHe';
import { CONSTANTS } from '@/utils/constants';

export interface ControllerPermission {
    name?: string;
    controller?: string;
    action?: string;
    create?: boolean;
    read?: boolean;
    update?: boolean;
    delete?: boolean;
    export?: boolean;
    import?: boolean;
    print?: boolean;
    allBP?: boolean;
    allBPCon?: boolean;
    allCT?: boolean;
    filter?: boolean;
    setupFormQT?: boolean;
    quanLy?: boolean;
    fullcontrol?: boolean;
}

/** Có ít nhất một quyền mở trang (xem / sửa / export / import / toàn quyền). */
export function hasControllerPageAccess(
    permission: ControllerPermission | null | undefined,
): boolean {
    return Boolean(
        permission?.read ||
        permission?.update ||
        permission?.export ||
        permission?.import ||
        permission?.fullcontrol,
    );
}

/**
 * Helper function để chuyển đổi role thành ControllerPermission
 */
const mergeModulePermissions = (roles: any[], controller: string): ControllerPermission | null => {
    if (!roles.length) return null;

    const merged: ControllerPermission = {
        controller,
        create: false,
        read: false,
        update: false,
        delete: false,
        export: false,
        import: false,
        print: false,
        allBP: false,
        allBPCon: false,
        allCT: false,
        filter: false,
        setupFormQT: false,
        quanLy: false,
        fullcontrol: false,
    };

    for (const role of roles) {
        const permission = mapRoleToPermission(role);
        if (permission.fullcontrol) {
            return {
                ...permission,
                controller,
                quanLy: Boolean(permission.quanLy || merged.quanLy),
            };
        }
        merged.create = merged.create || Boolean(permission.create);
        merged.read = merged.read || Boolean(permission.read);
        merged.update = merged.update || Boolean(permission.update);
        merged.delete = merged.delete || Boolean(permission.delete);
        merged.export = merged.export || Boolean(permission.export);
        merged.import = merged.import || Boolean(permission.import);
        merged.print = merged.print || Boolean(permission.print);
        merged.allBP = merged.allBP || Boolean(permission.allBP);
        merged.allBPCon = merged.allBPCon || Boolean(permission.allBPCon);
        merged.allCT = merged.allCT || Boolean(permission.allCT);
        merged.filter = merged.filter || Boolean(permission.filter);
        merged.setupFormQT = merged.setupFormQT || Boolean(permission.setupFormQT);
        merged.quanLy = merged.quanLy || Boolean(permission.quanLy);
    }

    return merged;
};

const GROUP_PARENT_NAME_PATTERNS: Record<string, RegExp> = {
    buoc5: /^B5:/i,
    buoc6: /^B6:/i,
};

/** Role trang thực tế — tránh role nhóm cha (B5/B6) làm bật allCT/allBP khi gộp org scope. */
export const pickPrimaryModuleRole = (listRole: any[], controller: string): any | null => {
    const roles = findRolesForController(listRole, controller);
    if (!roles.length) return null;

    const parentPattern = GROUP_PARENT_NAME_PATTERNS[controller];
    const leafRoles = parentPattern
        ? roles.filter((r) => !parentPattern.test(String(r.name ?? '').trim()))
        : roles;

    if (leafRoles.length === 1) return leafRoles[0];

    if (leafRoles.length > 1) {
        if (controller === 'buoc6') {
            const byAction = leafRoles.find((r) => r.action === 'b6.doluongchitieu');
            if (byAction) return byAction;
            const chiTieuNam = leafRoles.find((r) => String(r.name ?? '').includes('Chỉ tiêu năm'));
            if (chiTieuNam) return chiTieuNam;
        }
        return leafRoles[leafRoles.length - 1];
    }

    return roles[roles.length - 1];
};

const resolveActivePhanHe = (phanHe?: string | null) =>
    phanHe || getPhanHeFromStorage() || CONSTANTS.PHAN_HE.BSC;

/** Quyền module dùng cho lọc cơ cấu tổ chức (không gộp OR allCT từ role nhóm cha). */
export function getOrgScopeModulePermission(
    profile: any,
    controller: string | null | undefined,
    phanHe?: string | null,
): ControllerPermission | null {
    if (profile?.isAdmin) {
        return getModulePermission(profile, controller, undefined, phanHe);
    }

    const listRole = filterListRoleByPhanHe(profile?.listRole, resolveActivePhanHe(phanHe));
    if (!listRole.length || !controller) {
        return null;
    }

    const primary = pickPrimaryModuleRole(listRole as any[], controller);
    return primary ? mapRoleToPermission(primary) : null;
}

const findRolesForController = (
    listRole: any[],
    controller: string,
    action?: string | null
): any[] => {
    if (action) {
        return listRole.filter((r) => r.controller === controller && r.action === action);
    }
    if (controller === 'buoc6') {
        return listRole.filter(
            (r) => r.controller === controller && r.action && r.action === r.action.trim()
        );
    }
    return listRole.filter((r) => r.controller === controller);
};

export const mapRoleToPermission = (role: any): ControllerPermission => {
    const moduleFullControl = Boolean(role.fullControl ?? role.fullcontrol ?? role.FullControl);
    if (moduleFullControl) {
        return {
            name: role.name,
            controller: role.controller,
            action: role.action,
            create: true,
            read: true,
            update: true,
            delete: true,
            export: true,
            import: true,
            print: true,
            allBP: true,
            allBPCon: true,
            allCT: true,
            filter: true,
            setupFormQT: true,
            quanLy: role.quanLy ?? false,
            fullcontrol: true,
        } as ControllerPermission;
    }

    return {
        name: role.name,
        controller: role.controller,
        action: role.action,
        create: role.create ?? false,
        read: role.read ?? false,
        update: role.update ?? false,
        delete: role.delete ?? false,
        export: role.export ?? false,
        import: role.import ?? false,
        print: role.print ?? false,
        allBP: role.allBP ?? false,
        allBPCon: role.allBPCon ?? false,
        allCT: role.allCT ?? false,
        filter: role.filter ?? false,
        setupFormQT: role.setupFormQT ?? false,
        quanLy: role.quanLy ?? false,
        fullcontrol: false,
    } as ControllerPermission;
};

/** Lấy quyền module không cần hook (reload IndexedDB, util ngoài component). */
export function getModulePermission(
    profile: any,
    controller: string | null | undefined,
    action?: string | null | undefined,
    phanHe?: string | null,
): ControllerPermission | null {
    if (profile?.isAdmin) {
        return {
            name: 'FullControl',
            controller: controller ?? undefined,
            action: '',
            create: true,
            read: true,
            update: true,
            delete: true,
            export: true,
            import: true,
            print: true,
            allBP: true,
            allBPCon: true,
            allCT: true,
            filter: true,
            setupFormQT: true,
            quanLy: true,
            fullcontrol: true,
        } as ControllerPermission;
    }

    const listRole = filterListRoleByPhanHe(profile?.listRole, resolveActivePhanHe(phanHe));
    if (!listRole.length || !controller) {
        return null;
    }

    const roles = findRolesForController(listRole as any[], controller, action);
    return mergeModulePermissions(roles, controller);
}

const adminPermission = (controller?: string | null): ControllerPermission => ({
    name: 'FullControl',
    controller: controller ?? undefined,
    action: '',
    create: true,
    read: true,
    update: true,
    delete: true,
    export: true,
    import: true,
    print: true,
    allBP: true,
    allBPCon: true,
    allCT: true,
    filter: true,
    setupFormQT: true,
    quanLy: true,
    fullcontrol: true,
});

/** Gộp quyền từ mọi role khớp alias menu Văn hóa (vd. khaosatvanhoa.hientaituonglai ↔ vanhoa_dotkhaosat). */
export function getVanHoaModulePermission(
    profile: { isAdmin?: boolean; listRole?: unknown[]; isAdminVanHoa?: boolean } | null | undefined,
    moduleKeys: string[],
): ControllerPermission | null {
    if (profile?.isAdminVanHoa || profile?.isAdmin) {
        return adminPermission(moduleKeys[0]);
    }

    const listRole = filterListRoleByPhanHe(profile?.listRole, CONSTANTS.PHAN_HE.VAN_HOA);
    if (!listRole.length || moduleKeys.length === 0) {
        return null;
    }

    const expanded = expandVanHoaMenuModules(moduleKeys);
    const matchingRoles = (listRole as any[]).filter((role) => roleMatchesVanHoaMenuModules(role, expanded));
    if (matchingRoles.length === 0) {
        return null;
    }

    const merged: ControllerPermission = {
        create: false,
        read: false,
        update: false,
        delete: false,
        export: false,
        import: false,
        print: false,
        allBP: false,
        allBPCon: false,
        allCT: false,
        filter: false,
        setupFormQT: false,
        quanLy: false,
        fullcontrol: false,
    };

    for (const role of matchingRoles) {
        const permission = mapRoleToPermission(role);
        if (permission.fullcontrol) {
            return adminPermission(String(role.controller || moduleKeys[0]));
        }
        merged.create = merged.create || Boolean(permission.create);
        merged.read = merged.read || Boolean(permission.read);
        merged.update = merged.update || Boolean(permission.update);
        merged.delete = merged.delete || Boolean(permission.delete);
        merged.export = merged.export || Boolean(permission.export);
        merged.import = merged.import || Boolean(permission.import);
        merged.print = merged.print || Boolean(permission.print);
        merged.allBP = merged.allBP || Boolean(permission.allBP);
        merged.allBPCon = merged.allBPCon || Boolean(permission.allBPCon);
        merged.allCT = merged.allCT || Boolean(permission.allCT);
        merged.filter = merged.filter || Boolean(permission.filter);
        merged.setupFormQT = merged.setupFormQT || Boolean(permission.setupFormQT);
        merged.quanLy = merged.quanLy || Boolean(permission.quanLy);
    }

    return merged;
}

export function useVanHoaPermission(moduleKeys: string | string[]): ControllerPermission | null {
    const profile = useAuthStore((state) => state.profile);

    return useMemo(() => {
        const keys = (Array.isArray(moduleKeys) ? moduleKeys : moduleKeys.split(','))
            .map((item) => item.trim())
            .filter(Boolean);
        return getVanHoaModulePermission(profile, keys);
    }, [moduleKeys, profile]);
}

export function usePermission(controller: string | null | undefined, action?: string | null): ControllerPermission | null;
export function usePermission(controllers: (string | null)[]): Record<string, ControllerPermission>;
export function usePermission(
    controllerOrControllers: string | null | undefined | (string | null | undefined)[],
    action?: string | null | undefined
): ControllerPermission | null | Record<string, ControllerPermission | null> {
    const profile = useAuthStore((state) => state.profile);
    const activePhanHe = useGetPhanHe();

    return useMemo(() => {
        let roleByModule = null;

        if (profile?.isAdmin) {
            roleByModule = {
                name: "FullControl",
                controller: controllerOrControllers,
                action: '',
                create: true,
                read: true,
                update: true,
                delete: true,
                export: true,
                import: true,
                print: true,
                allBP: true,
                allBPCon: true,
                allCT: true,
                filter: true,
                setupFormQT: true,
                quanLy: true,
                fullcontrol: true,
            } as ControllerPermission;
        } else {
            const listRole = filterListRoleByPhanHe(profile?.listRole, activePhanHe);

            if (!listRole.length) {
                if (Array.isArray(controllerOrControllers)) {
                    return {} as Record<string, ControllerPermission | null>;
                }
                return null;
            }
            if (Array.isArray(controllerOrControllers)) {
                const result: Record<string, ControllerPermission | null> = {};
                controllerOrControllers.forEach((controller) => {
                    if (!controller) {
                        return;
                    }
                    const roles = findRolesForController(listRole as any[], controller);
                    result[controller] = mergeModulePermissions(roles, controller);
                });
                return result;
            }

            if (!controllerOrControllers) {
                return null;
            }
            const roles = findRolesForController(
                listRole as any[],
                controllerOrControllers,
                action
            );

            roleByModule = mergeModulePermissions(roles, controllerOrControllers);
        }
        return roleByModule;
    }, [controllerOrControllers, action, profile?.listRole, profile?.isAdmin, activePhanHe]);
}
